import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Save, ArrowLeft, Trash2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";

const PLAN_OPTIONS = [
  { id: "free", label: "Free" },
  { id: "hobby", label: "Hobby" },
  { id: "business", label: "Business" },
];
const SLAB_OPTIONS = [
  { id: "", label: "None" },
  { id: "starter", label: "Starter (250)" },
  { id: "pro", label: "Pro (1,000)" },
  { id: "unlimited", label: "Unlimited (5,000)" },
];

export default function Admin() {
  const { sub } = useSubscription();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [savingId, setSavingId] = useState("");
  const [confirmId, setConfirmId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [trialSel, setTrialSel] = useState({});
  const [trialId, setTrialId] = useState("");

  const load = () => api.get("/admin/subscriptions").then(({ data }) => setRows(data)).catch((e) => {
    toast.error(formatApiError(e.response?.data?.detail));
    setRows([]);
  });

  useEffect(() => { load(); }, []);

  // Not a superadmin → bounce out once we know.
  useEffect(() => {
    if (sub && sub.is_superadmin === false) {
      toast.error("Admin access only");
      navigate("/");
    }
  }, [sub, navigate]);

  const patch = (ownerId, field, value) => {
    setRows((r) => r.map((x) => x.owner_id === ownerId
      ? (field === "plan" || field === "status"
          ? { ...x, [field]: value }
          : { ...x, addons: { ...x.addons, [field]: value } })
      : x));
  };

  const save = async (row) => {
    setSavingId(row.owner_id);
    try {
      await api.put(`/admin/subscriptions/${row.owner_id}`, {
        plan: row.plan,
        status: row.status,
        slab: row.addons?.slab || "",
        shopify: !!row.addons?.shopify,
        boxed: !!row.addons?.boxed,
      });
      toast.success("Subscription updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingId("");
    }
  };

  const removeAccount = async (row) => {
    setDeletingId(row.owner_id);
    try {
      const { data } = await api.delete(`/admin/accounts/${row.owner_id}`);
      toast.success(`Deleted ${data.email} — ${data.users_removed} user(s) and all their data removed`);
      setConfirmId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeletingId("");
    }
  };

  const setTrial = (ownerId, field, value) =>
    setTrialSel((t) => ({ ...t, [ownerId]: { ...(t[ownerId] || {}), [field]: value } }));

  const startTrial = async (row) => {
    const sel = trialSel[row.owner_id] || {};
    const plan = sel.plan || "business";
    const days = Number(sel.days) || 14;
    setTrialId(row.owner_id);
    try {
      await api.put(`/admin/subscriptions/${row.owner_id}`, { trial_plan: plan, trial_days: days });
      toast.success(`Started a ${days}-day ${plan} trial`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTrialId(""); }
  };

  const endTrial = async (row) => {
    setTrialId(row.owner_id);
    try {
      await api.put(`/admin/subscriptions/${row.owner_id}`, { trial_plan: "", trial_days: 0 });
      toast.success("Trial ended");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTrialId(""); }
  };

  if (!rows) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pk-bg)" }}>
      <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
    </div>;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--pk-bg)" }} data-testid="admin-page">
      <header className="border-b" style={{ borderColor: "var(--pk-border)", background: "var(--pk-surface)" }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-5 w-5 text-amber-400" />
            <span className="font-heading font-extrabold text-lg text-slate-100">Admin — Subscriptions</span>
          </div>
          <button onClick={() => navigate("/")} data-testid="admin-back"
            className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to app
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <p className="text-slate-400 text-sm mb-5">{rows.length} vendor account{rows.length === 1 ? "" : "s"}. Edit a plan or add-ons and press save.</p>
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.owner_id} data-testid={`admin-row-${r.owner_id}`}
              className="rounded-xl border p-4 sm:p-5" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="min-w-0 lg:w-56">
                  <div className="text-sm font-semibold text-slate-100 truncate">{r.name || r.email.split("@")[0]}</div>
                  <div className="text-xs text-slate-500 truncate">{r.email}</div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Inv {r.inventory.toLocaleString()}{r.inventory_cap != null ? `/${r.inventory_cap.toLocaleString()}` : "/∞"} · Users {r.users}/{r.max_users} · Slabs {r.slab_used}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 flex-1">
                  <Field label="Plan">
                    <select data-testid={`admin-plan-${r.owner_id}`} value={r.plan} onChange={(e) => patch(r.owner_id, "plan", e.target.value)}
                      className="bg-transparent text-sm text-slate-100 outline-none">
                      {PLAN_OPTIONS.map((o) => <option key={o.id} value={o.id} className="bg-[#181D2A]">{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Status">
                    <select data-testid={`admin-status-${r.owner_id}`} value={r.status} onChange={(e) => patch(r.owner_id, "status", e.target.value)}
                      className="bg-transparent text-sm text-slate-100 outline-none">
                      {["active", "past_due", "canceled"].map((s) => <option key={s} value={s} className="bg-[#181D2A]">{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Slab">
                    <select data-testid={`admin-slab-${r.owner_id}`} value={r.addons?.slab || ""} onChange={(e) => patch(r.owner_id, "slab", e.target.value)}
                      className="bg-transparent text-sm text-slate-100 outline-none">
                      {SLAB_OPTIONS.map((o) => <option key={o.id} value={o.id} className="bg-[#181D2A]">{o.label}</option>)}
                    </select>
                  </Field>
                  <Toggle label="Shopify" checked={!!r.addons?.shopify} testid={`admin-shopify-${r.owner_id}`} onChange={(v) => patch(r.owner_id, "shopify", v)} />
                  <Toggle label="Boxed" checked={!!r.addons?.boxed} testid={`admin-boxed-${r.owner_id}`} onChange={(v) => patch(r.owner_id, "boxed", v)} />
                </div>

                <button data-testid={`admin-save-${r.owner_id}`} onClick={() => save(r)} disabled={savingId === r.owner_id}
                  className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors disabled:opacity-60 flex items-center gap-2">
                  {savingId === r.owner_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </button>
                {r.is_self ? null : confirmId === r.owner_id ? (
                  <div className="shrink-0 flex items-center gap-2" data-testid={`admin-confirm-wrap-${r.owner_id}`}>
                    <button data-testid={`admin-delete-confirm-${r.owner_id}`} onClick={() => removeAccount(r)} disabled={deletingId === r.owner_id}
                      className="px-3 py-2 rounded-lg text-sm font-semibold bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-60 flex items-center gap-2">
                      {deletingId === r.owner_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Confirm
                    </button>
                    <button data-testid={`admin-delete-cancel-${r.owner_id}`} onClick={() => setConfirmId("")}
                      className="px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-100 border" style={{ borderColor: "var(--pk-border)" }}>Cancel</button>
                  </div>
                ) : (
                  <button data-testid={`admin-delete-${r.owner_id}`} onClick={() => setConfirmId(r.owner_id)} title="Delete account & all data"
                    className="shrink-0 px-3 py-2 rounded-lg text-sm font-semibold text-rose-300 border border-rose-500/40 hover:bg-rose-500/10 flex items-center gap-2">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                )}
              </div>

              <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-3" style={{ borderColor: "var(--pk-border)" }} data-testid={`admin-trial-wrap-${r.owner_id}`}>
                {r.trial_active ? (
                  <>
                    <span className="text-xs text-emerald-400 font-medium flex items-center gap-1.5" data-testid={`admin-trial-active-${r.owner_id}`}>
                      <Clock className="h-3.5 w-3.5" /> On {r.trial_plan} trial · ends {r.trial_ends_at ? new Date(r.trial_ends_at).toLocaleDateString() : ""}
                    </span>
                    <button data-testid={`admin-trial-end-${r.owner_id}`} onClick={() => endTrial(r)} disabled={trialId === r.owner_id}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-300 border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-60">End trial</button>
                  </>
                ) : (
                  <>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Free trial</span>
                    <select data-testid={`admin-trial-plan-${r.owner_id}`} value={(trialSel[r.owner_id]?.plan) || "business"} onChange={(e) => setTrial(r.owner_id, "plan", e.target.value)}
                      className="bg-transparent text-sm text-slate-100 outline-none border rounded-lg px-2.5 py-1.5" style={{ borderColor: "var(--pk-border)" }}>
                      {PLAN_OPTIONS.filter((o) => o.id !== "free").map((o) => <option key={o.id} value={o.id} className="bg-[#181D2A]">{o.label}</option>)}
                    </select>
                    <input data-testid={`admin-trial-days-${r.owner_id}`} type="number" min="1" placeholder="14"
                      value={(trialSel[r.owner_id]?.days) ?? ""} onChange={(e) => setTrial(r.owner_id, "days", e.target.value)}
                      className="w-20 bg-transparent text-sm text-slate-100 outline-none border rounded-lg px-2.5 py-1.5" style={{ borderColor: "var(--pk-border)" }} />
                    <span className="text-xs text-slate-500">days</span>
                    <button data-testid={`admin-trial-start-${r.owner_id}`} onClick={() => startTrial(r)} disabled={trialId === r.owner_id}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/10 disabled:opacity-60 flex items-center gap-1.5">
                      {trialId === r.owner_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5" />} Start trial
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <div className="mt-0.5 px-2.5 py-1.5 rounded-lg border" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        {children}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${checked ? "text-amber-400 border-amber-500/40 bg-amber-500/10" : "text-slate-400 border-[var(--pk-border)]"}`}>
      <span className={`h-3.5 w-3.5 rounded-full ${checked ? "bg-amber-400" : "bg-slate-600"}`} /> {label}
    </button>
  );
}
